// src/QuoteManagement.js
import React, { useState, useMemo, useEffect } from "react";
import { Button, Table, Modal, Form, Card } from "react-bootstrap";
import axios from "axios";
import { BASE_URL, security_key } from "../../../global";
import Swal from "sweetalert2";
import LinearProgress from "@mui/material/LinearProgress";

const QuoteManagement = ({setactive_quote_change}) => {
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [quotes, setQuotes] = useState([]);
    const [activeQuote, setActiveQuote] = useState(null);
    const [selectedQuote, setSelectedQuote] = useState(null);

    // Modal states for Create/Edit
    const [showViewModal, setShowViewModal] = useState(false);
    const [showFormModal, setShowFormModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({
        pk_quote_key: "",
        quote: "",
        author_name: "",
    });

    const [sortConfig, setSortConfig] = useState({
        key: "quote",
        direction: "asc",
    });
    const [currentPage, setCurrentPage] = useState(1);

    // Fetch all quotes and active quote on mount
    useEffect(() => {
        fetchAllQuotes();
    }, []);

    const fetchAllQuotes = async () => {
        setLoading(true);
        try {
            // 1. Fetch All Visible Quotes
            const resAll = await axios.post(BASE_URL + "API/apicall", {
                security_key: security_key,
                mode: "getAllQuote",
            });

            if (Array.isArray(resAll.data)) {
                setQuotes(resAll.data);
                // Find the currently active quote from the list
                const currentActive = resAll.data.find((q) => q.is_active == 1 || q.is_active === "1");
                setActiveQuote(currentActive || null);
            } else {
                setQuotes([]);
            }
        } catch (error) {
            console.error("Error fetching quotes:", error);
        } finally {
            setLoading(false);
        }
    };

    // Set selected quote as active
    const handleSetActive = async (key) => {
        try {
            const response = await axios.post(BASE_URL + "API/apicall", {
                security_key: security_key,
                mode: "setActiveQuote",
                pk_quote_key: key,
                modified_by: 1, // Pass dynamic user ID if available
            });

            if (response.data && response.data.status === 1) {
                Swal.fire({
                    icon: "success",
                    title: "Activated!",
                    text: "Active quote updated successfully.",
                    toast: true,
                    position: "top-end",
                    showConfirmButton: false,
                    timer: 2000,
                });
                setactive_quote_change(p => p+1)
                fetchAllQuotes();
            }
        } catch (error) {
            console.error("Error setting active quote:", error);
            Swal.fire("Error", "Failed to activate quote", "error");
        }
    };

    // Handle Delete (Soft Delete)
    const handleDelete = async (quote_key) => {
        try {
            const result = await Swal.fire({
                title: "Are you sure?",
                text: "This quote will be removed from view!",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
                confirmButtonText: "Yes, delete it",
            });

            if (result.isConfirmed) {
                const response = await axios.post(BASE_URL + "API/apicall", {
                    security_key: security_key,
                    mode: "deleteQuote",
                    pk_quote_key: quote_key,
                    modified_by: 1,
                });

                if (response.data && response.data.status === 1) {
                    await Swal.fire("Deleted!", "The quote has been removed.", "success");
                    fetchAllQuotes();
                } else {
                    Swal.fire("Error", "Could not delete quote", "error");
                }
            }
        } catch (error) {
            console.error("An error occurred: ", error);
        }
    };

    // Open Form Modal for Creating a New Quote
    const handleOpenAddModal = () => {
        setIsEditing(false);
        setFormData({ pk_quote_key: "", quote: "", author_name: "" });
        setShowFormModal(true);
    };

    // Open Form Modal for Editing an Existing Quote
    const handleOpenEditModal = (q) => {
        setIsEditing(true);
        setFormData({
            pk_quote_key: q.pk_quote_key,
            quote: q.quote,
            author_name: q.author_name || "",
        });
        setShowFormModal(true);
    };

    // Submit Handler for Add / Update
    const handleFormSubmit = async (e) => {
        e.preventDefault();
        try {
            const mode = isEditing ? "updateQuote" : "addQuote";
            const payload = {
                security_key: security_key,
                mode: mode,
                quote: formData.quote,
                author_name: formData.author_name,
                ...(isEditing ? { pk_quote_key: formData.pk_quote_key, modified_by: 1 } : { created_by: 1 }),
            };

            const response = await axios.post(BASE_URL + "API/apicall", payload);

            if (response.data && response.data.status === 1) {
                Swal.fire("Success", `Quote ${isEditing ? "updated" : "added"} successfully!`, "success");
                setShowFormModal(false);
                fetchAllQuotes();
            } else {
                Swal.fire("Error", response.data.message || "Failed to save quote", "error");
            }
        } catch (error) {
            console.error("Form submit error:", error);
            Swal.fire("Error", "Something went wrong", "error");
        }
    };

    // Pagination & Filtering logic
    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const filteredQuotes = useMemo(() => {
        return quotes.filter((q) => {
            const term = searchTerm.toLowerCase();
            const quoteMatch = q.quote ? q.quote.toLowerCase().includes(term) : false;
            const authorMatch = q.author_name ? q.author_name.toLowerCase().includes(term) : false;
            return quoteMatch || authorMatch;
        });
    }, [quotes, searchTerm]);

    const sortedQuotes = useMemo(() => {
        const sorted = [...filteredQuotes];
        sorted.sort((a, b) => {
            const valA = (a[sortConfig.key] || "").toString().toLowerCase();
            const valB = (b[sortConfig.key] || "").toString().toLowerCase();
            if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
            if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
            return 0;
        });
        return sorted;
    }, [filteredQuotes, sortConfig]);

    const ITEMS_PER_PAGE = 10;

    const paginatedQuotes = useMemo(() => {
        const start = (currentPage - 1) * ITEMS_PER_PAGE;
        return sortedQuotes.slice(start, start + ITEMS_PER_PAGE);
    }, [sortedQuotes, currentPage]);

    const totalPages = Math.ceil(sortedQuotes.length / ITEMS_PER_PAGE) || 1;

    const handleSort = (key) => {
        setSortConfig((prev) => ({
            key,
            direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
        }));
    };

    const handleShowViewModal = (quote) => {
        setSelectedQuote(quote);
        setShowViewModal(true);
    };

    return (
        <div className="container mt-4">
            <Card className="mb-4 shadow-sm border-0 bg-white">
                <Card.Body className="p-3">
                    <div className="d-flex align-items-center mb-1">
                        <span className="badge bg-success me-2">ACTIVE QUOTE</span>
                    </div>
                    {activeQuote ? (
                        <figure
                            className="quote-card"
                            style={{
                                margin: "0 auto",
                                textAlign: "center",
                                maxWidth: "700px",
                            }}
                        >
                            <p className="quote-text">
                                <span className="quote-mark">“</span>
                                {activeQuote.quote}
                                <span className="quote-mark">”</span>
                            </p>

                            {activeQuote.author_name && (
                                <figcaption className="quote-author">
                                    {activeQuote.author_name}
                                </figcaption>
                            )}
                        </figure>
                    ) : (
                        <p className="text-muted mb-0 mt-1 fst-italic">
                            <i className="fa-solid fa-circle-info me-2"></i>
                            No active quote selected. Select a radio button in the list below to activate one.
                        </p>
                    )}
                </Card.Body>
            </Card>

            {/* --- PAGE HEADER --- */}
            <div className="d-flex justify-content-between align-items-center admin-page-head mb-3">
                <h4 className="mb-0">Quotes Management</h4>
                <Button variant="dark" onClick={handleOpenAddModal}>
                    <i className="fa-solid fa-plus me-2"></i>Add New Quote
                </Button>
            </div>

            {/* --- SEARCH FILTER --- */}
            <div className="mb-3">
                <Form>
                    <div className="row">
                        <div className="col-md-4">
                            <Form.Group>
                                <Form.Control
                                    type="text"
                                    placeholder="Search by quote or author..."
                                    value={searchTerm}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                />
                            </Form.Group>
                        </div>
                    </div>
                </Form>
            </div>

            {/* --- DATA TABLE --- */}
            <div className="table-responsive">
                <Table bordered striped hover align="middle">
                    <thead className="thead-dark">
                        <tr>
                            
                            <th onClick={() => handleSort("quote")} style={{ cursor: "pointer" }}>
                                Quote{" "}
                                {sortConfig.key === "quote" && (
                                    <i className={`fa-solid fa-caret-${sortConfig.direction === "asc" ? "up" : "down"}`}></i>
                                )}
                            </th>
                            <th onClick={() => handleSort("author_name")} style={{ cursor: "pointer", width: "200px" }}>
                                Author{" "}
                                {sortConfig.key === "author_name" && (
                                    <i className={`fa-solid fa-caret-${sortConfig.direction === "asc" ? "up" : "down"}`}></i>
                                )}
                            </th>
                            <th style={{ width: "80px" }} className="text-center">
                                Active
                            </th>
                            <th style={{ width: "150px" }} className="text-center">
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <td colSpan={4} className="p-0">
                                    <LinearProgress />
                                </td>
                            </tr>
                        )}

                        {!loading && paginatedQuotes?.length === 0 && (
                            <tr>
                                <td colSpan={4}>
                                    <h6 className="text-center my-2 text-muted">No quotes available</h6>
                                </td>
                            </tr>
                        )}

                        {!loading &&
                            paginatedQuotes?.map((item) => {
                                const isActive = item.is_active == 1 || item.is_active === "1";
                                return (
                                    <tr key={item.pk_quote_key}>
                                       
                                        <td>
                                            <div>{item.quote}</div>

                                        </td>
                                        <td>{item.author_name || <em className="text-muted">None</em>}</td>
                                         <td className="text-center">
                                            <Form.Check
                                                type="radio"
                                                name="activeQuoteRadio"
                                                checked={isActive}
                                                onChange={() => handleSetActive(item.pk_quote_key)}
                                                title="Set as Active Quote"
                                                style={{ cursor: "pointer" }}
                                            />
                                        </td>
                                        <td className="text-center">
                                            <div className="actbtn-grp justify-content-center">
                                                <Button
                                                    onClick={() => handleOpenEditModal(item)}
                                                    className="edit-icon btn btn-outline-dark btn-sm"
                                                    title="Edit"
                                                >
                                                    <i className="fa-solid fa-pen-to-square"></i>
                                                </Button>
                                                <Button
                                                    onClick={() => handleDelete(item.pk_quote_key)}
                                                    className="btn btn-outline-danger btn-sm"
                                                    title="Delete"
                                                    disabled={isActive}
                                                >
                                                    <i className="fa-solid fa-trash"></i>
                                                </Button>
                                                <Button
                                                    onClick={() => handleShowViewModal(item)}
                                                    className="btn btn-outline-info btn-sm"
                                                    title="View"
                                                >
                                                    <i className="fa-solid fa-eye"></i>
                                                </Button>
                                            </div>
                                        </td>
                                        
                                    </tr>
                                );
                            })}
                    </tbody>
                </Table>
            </div>

            {/* --- PAGINATION --- */}
            <div className="pagination d-flex justify-content-end my-3">
                <Button onClick={() => handlePageChange(1)} disabled={currentPage === 1} className="btn btn-light mx-1">
                    &lt;&lt;
                </Button>
                <Button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="btn btn-light mx-1">
                    &lt;
                </Button>
                {Array.from({ length: totalPages }, (_, index) => (
                    <Button
                        key={index + 1}
                        onClick={() => handlePageChange(index + 1)}
                        className={`mx-1 ${currentPage === index + 1 ? "btn-dark text-white" : "btn-light"}`}
                    >
                        {index + 1}
                    </Button>
                ))}
                <Button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="btn btn-light mx-1">
                    &gt;
                </Button>
                <Button onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} className="btn btn-light mx-1">
                    &gt;&gt;
                </Button>
            </div>

            {/* --- ADD / EDIT FORM MODAL --- */}
            <Modal show={showFormModal} onHide={() => setShowFormModal(false)} backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>{isEditing ? "Edit Quote" : "Add New Quote"}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleFormSubmit}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Quote <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={4}
                                placeholder="Enter quote here..."
                                value={formData.quote}
                                onChange={(e) => setFormData({ ...formData, quote: e.target.value })}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Author Name <small className="text-muted">(Optional)</small></Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="e.g. Albert Einstein"
                                value={formData.author_name}
                                onChange={(e) => setFormData({ ...formData, author_name: e.target.value })}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowFormModal(false)}>
                            Cancel
                        </Button>
                        <Button variant="dark" type="submit">
                            {isEditing ? "Update Quote" : "Save Quote"}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* --- VIEW DETAIL MODAL --- */}
            {selectedQuote && (
                <Modal show={showViewModal} onHide={() => setShowViewModal(false)}>
                    <Modal.Header closeButton>
                        <Modal.Title>Quote Details</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <h5>Quote</h5>



                        <figure
                            className="quote-card"
                            style={{
                                margin: "0 auto",
                                textAlign: "center",
                                maxWidth: "700px",
                            }}
                        >
                            <p className="quote-text">
                                <span className="quote-mark">“</span>
                                {selectedQuote.quote}
                                <span className="quote-mark">”</span>
                            </p>

                            {selectedQuote.author_name && (
                                <figcaption className="quote-author">
                                    {selectedQuote.author_name}
                                </figcaption>
                            )}
                        </figure>


                        <h5>Status</h5>
                        <p>
                            {selectedQuote.is_active == 1 ? (
                                <span className="badge bg-success">Currently Active</span>
                            ) : (
                                <span className="badge bg-secondary">Inactive</span>
                            )}
                        </p>

                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowViewModal(false)}>
                            Close
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}

            {/* Custom Styles matching your existing template */}
            <style jsx>
                {`
          th {
            cursor: pointer;
          }
          .pagination {
            margin-top: 20px;
          }
          .edit-icon:hover {
            color: #fff !important;
          }
          .actbtn-grp {
            display: flex;
            gap: 6px;
            flex-wrap: wrap;
          }


        `}
            </style>
        </div>
    );
};

export default QuoteManagement;